import React, { useState } from "react";
import { BookOpen, Award, RefreshCw } from 'lucide-react';
import { supabase } from './lib/supabase';
import { useAuth } from './contexts/AuthContext';

function Quiz() {
  const { user } = useAuth();
  const [topic, setTopic] = useState("");
  const [quiz, setQuiz] = useState(null);
  const [selectedOption, setSelectedOption] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [questionCount, setQuestionCount] = useState(0);
  const [correctAnswers, setCorrectAnswers] = useState(0);
  const [quizHistory, setQuizHistory] = useState([]);
  const [showSummary, setShowSummary] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [error, setError] = useState("");

  const QUIZ_SESSION_LENGTH = 5;

  const saveQuizScore = async () => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('quiz_scores')
        .insert({
          user_id: user.id,
          topic: topic,
          score: correctAnswers,
          total_questions: QUIZ_SESSION_LENGTH
        });

      if (error) throw error;
    } catch (err) {
      console.error('Error saving quiz score:', err);
    }
  };

  const resetQuizSession = () => {
    setQuizHistory([]);
    setCorrectAnswers(0);
    setQuestionCount(0);
    setShowSummary(false);
    setFeedback("");
    setQuiz(null);
    setResult(null);
    setError("");
  };

  const fetchQuiz = async () => {
    if (!topic.trim()) {
      setError("Please enter a topic first!");
      return;
    }

    setError("");
    setLoading(true);
    try {
      const res = await fetch("https://aitutorbackend-production.up.railway.app/quiz/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ topic }),
      });

      if (!res.ok) {
        throw new Error(`HTTP error! Status: ${res.status}`);
      }

      const data = await res.json();
      if (data.error) {
        throw new Error(data.error);
      }

      setQuiz(data.quiz);
      setSelectedOption("");
      setResult(null);

      if (!showSummary) {
        setQuestionCount(prev => prev + 1);
      }
    } catch (error) {
      console.error("Error fetching quiz:", error);
      setResult("Failed to fetch quiz. Please try again.");
    }
    setLoading(false);
  };

  const checkAnswer = () => {
    if (!selectedOption) {
      setResult("Please select an answer first!");
      return;
    }

    const isCorrect = selectedOption === quiz.answer;

    if (isCorrect) {
      setResult("Correct!");
      setCorrectAnswers(prev => prev + 1);
    } else {
      setResult(`Incorrect. The correct answer is: ${quiz.answer}`);
    }

    setQuizHistory(prev => [
      ...prev,
      {
        question: quiz.question,
        userAnswer: selectedOption,
        correctAnswer: quiz.answer,
        isCorrect
      }
    ]);

    if (questionCount >= QUIZ_SESSION_LENGTH) {
      generateFeedback();
      setShowSummary(true);
      // Save quiz score to Supabase when quiz is completed
      saveQuizScore();
    }
  };

  const generateFeedback = () => {
    const accuracy = ((correctAnswers + (selectedOption === quiz.answer ? 1 : 0)) / QUIZ_SESSION_LENGTH) * 100;
    let feedbackMessage = "";

    if (accuracy >= 90) {
      feedbackMessage = `Outstanding! You achieved ${accuracy.toFixed(0)}% accuracy. You're mastering ${topic}!`;
    } else if (accuracy >= 70) {
      feedbackMessage = `Great work! You scored ${accuracy.toFixed(0)}%. You have a solid understanding of ${topic}.`;
    } else if (accuracy >= 50) {
      feedbackMessage = `Good effort! You got ${accuracy.toFixed(0)}%. Keep practicing ${topic} to improve further.`;
    } else {
      feedbackMessage = `You're making progress with ${topic}. Remember, every question is a learning opportunity!`;
    }

    if (quizHistory.length >= 3) {
      const recentCorrect = quizHistory.slice(-3).filter(item => item.isCorrect).length;
      const earlierCorrect = quizHistory.slice(0, 3).filter(item => item.isCorrect).length;

      if (recentCorrect > earlierCorrect) {
        feedbackMessage += " Your performance is improving - keep it up!";
      }
    }

    setFeedback(feedbackMessage);
  };

  const handleNextQuestion = () => {
    if (showSummary) {
      resetQuizSession();
    }
    fetchQuiz();
  };

  return (
    <div className="min-h-screen bg-parchment pt-16 font-serif">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-4">
            <BookOpen className="h-16 w-16 text-parchment-dark" />
          </div>
          <h1 className="vintage-title">
            Historical Quiz
          </h1>
          <p className="vintage-subtitle max-w-3xl mx-auto">
            Test your knowledge of historical events and learn something new
          </p>
        </div>

        {/* Quiz Progress */}
        {questionCount > 0 && !showSummary && (
          <div className="vintage-card mb-8">
            <div className="flex justify-between items-center">
              <span className="text-parchment-dark">
                Question {questionCount} of {QUIZ_SESSION_LENGTH}
              </span>
              <span className="text-parchment-dark font-medium">
                Score: {correctAnswers}/{questionCount - 1}
              </span>
            </div>
            <div className="mt-2 h-2 bg-parchment rounded-full overflow-hidden">
              <div
                className="h-2 bg-parchment-dark rounded-full transition-all duration-300"
                style={{ width: `${(questionCount / QUIZ_SESSION_LENGTH) * 100}%` }}
              ></div>
            </div>
          </div>
        )}

        {/* Topic Selection */}
        {(!quiz || showSummary) && (
          <div className="vintage-card">
            <div className="max-w-md mx-auto">
              <input
                type="text"
                className="vintage-input"
                placeholder="Enter a topic (e.g., World War II, Ancient Egypt)"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
              />
              <button
                onClick={fetchQuiz}
                disabled={loading}
                className="vintage-button w-full mt-4 flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <RefreshCw className="h-5 w-5 animate-spin" />
                    Generating Quiz...
                  </>
                ) : (
                  <>
                    <BookOpen className="h-5 w-5" />
                    Start Quiz
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Quiz Summary */}
        {showSummary && (
          <div className="vintage-card">
            <div className="text-center">
              <Award className="h-16 w-16 text-parchment-dark mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-parchment-dark mb-4">Quiz Complete!</h2>
              <div className="mb-6">
                <div className="text-5xl font-bold text-parchment-dark">
                  {Math.round((correctAnswers / QUIZ_SESSION_LENGTH) * 100)}%
                </div>
                <p className="text-parchment-dark/80 mt-2">
                  You answered {correctAnswers} out of {QUIZ_SESSION_LENGTH} questions correctly
                </p>
              </div>
              <div className="bg-parchment rounded-lg p-4 mb-6 border-2 border-parchment-dark/20">
                <p className="text-parchment-dark">{feedback}</p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() => {
                    resetQuizSession();
                    fetchQuiz();
                  }}
                  className="vintage-button"
                >
                  Try Again
                </button>
                <button
                  onClick={() => {
                    resetQuizSession();
                    setTopic("");
                  }}
                  className="vintage-button"
                >
                  Choose New Topic
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Quiz Question */}
        {quiz && !showSummary && (
          <div className="vintage-card">
            <h2 className="text-xl font-semibold text-parchment-dark mb-6">
              {quiz.question}
            </h2>
            <div className="space-y-3">
              {quiz.options.map((option, index) => (
                <label
                  key={index}
                  className={`block p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                    selectedOption === option
                      ? 'border-parchment-dark bg-parchment'
                      : 'border-parchment-dark/20 hover:border-parchment-dark/40 hover:bg-parchment-light'
                  }`}
                >
                  <div className="flex items-center">
                    <input
                      type="radio"
                      name="quiz-option"
                      value={option}
                      checked={selectedOption === option}
                      onChange={(e) => setSelectedOption(e.target.value)}
                      className="h-4 w-4 text-parchment-dark focus:ring-parchment-dark"
                    />
                    <span className="ml-3 text-parchment-dark">{option}</span>
                  </div>
                </label>
              ))}
            </div>

            <div className="mt-8">
              {!result && (
                <button
                  onClick={checkAnswer}
                  disabled={!selectedOption}
                  className="vintage-button w-full"
                >
                  Submit Answer
                </button>
              )}

              {result && (
                <div className="space-y-4">
                  <div className={`p-4 rounded-lg ${
                    result.startsWith('Correct')
                      ? 'bg-green-50 text-green-800 border-2 border-green-200'
                      : 'bg-red-50 text-red-800 border-2 border-red-200'
                  }`}>
                    {result}
                  </div>
                  <button
                    onClick={handleNextQuestion}
                    className="vintage-button w-full"
                  >
                    {questionCount >= QUIZ_SESSION_LENGTH ? "View Results" : "Next Question"}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Quiz;